const SSO_NONCE_KEY = "cennaWordAddInSsoNonce";

/** Generate a random nonce for the Auth0 implicit flow. */
function generateNonce(): string {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

/** Store the nonce in sessionStorage before redirecting to Auth0. */
export function storeSsoNonce(nonce: string): void {
  try {
    sessionStorage.setItem(SSO_NONCE_KEY, nonce);
  } catch {
    // ignore
  }
}

/** Retrieve and clear the nonce after Auth0 redirects back. */
export function consumeSsoNonce(): string | null {
  try {
    const nonce = sessionStorage.getItem(SSO_NONCE_KEY);
    sessionStorage.removeItem(SSO_NONCE_KEY);
    return nonce;
  } catch {
    return null;
  }
}

/**
 * Build the Auth0 ``/authorize`` URL for the implicit flow.
 *
 * @param auth0Domain  e.g. ``my-tenant.auth0.com``
 * @param clientId     Auth0 application client ID
 * @returns The full authorize URL to redirect to; a nonce is stored in sessionStorage.
 */
export function buildAuth0AuthorizeUrl(
  auth0Domain: string,
  clientId: string,
  connection?: string,
): string {
  const nonce = generateNonce();
  storeSsoNonce(nonce);

  const redirectUri = `${window.location.origin}/auth-callback.html`;

  const params = new URLSearchParams({
    response_type: "id_token",
    client_id: clientId,
    redirect_uri: redirectUri,
    scope: "openid profile email",
    nonce,
  });

  const trimmedConnection = connection?.trim();
  if (trimmedConnection) {
    params.set("connection", trimmedConnection);
  }

  const domain = auth0Domain.replace(/\/+$/, "");
  return `https://${domain}/authorize?${params.toString()}`;
}

/**
 * Extract the ``id_token`` from the URL hash fragment after Auth0 redirects back.
 * Auth0 implicit flow returns ``#id_token=...&token_type=...``.
 */
export function extractIdTokenFromHash(): string | null {
  const hash = window.location.hash;
  if (!hash || hash.length < 2) return null;

  const params = new URLSearchParams(hash.substring(1));
  return params.get("id_token") || null;
}

/** Remove the hash fragment from the URL without triggering a page reload. */
export function clearUrlHash(): void {
  window.history.replaceState(null, "", window.location.pathname + window.location.search);
}
